import React, { useEffect, useState } from "react";
import { Modal, Row, Col, Form } from "react-bootstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { apiService } from "service/ApiService";
import { notificationService } from "service/NotificationService";
import SaveButton from "components/Buttons/SaveButton";

import { useTranslation } from "react-i18next";

function PriceInput({ value, onCommit }) {
  const [draft, setDraft] = useState(value.toFixed(2));

  useEffect(() => {
    setDraft(value.toFixed(2));
  }, [value]);

  const commit = () => {
    const price = Number(draft);
    if (draft === "" || Number.isNaN(price) || price < 0 || price === value) {
      setDraft(value.toFixed(2));
      return;
    }
    onCommit(price).catch(() => setDraft(value.toFixed(2)));
  };

  return (
    <div className="d-flex align-items-center">
      <Form.Control
        type="number"
        step="0.01"
        min="0"
        size="sm"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.target.blur();
        }}
      />
      <span className="ml-1">€</span>
    </div>
  );
}

export default function ProductsOrderModal({ categoryId, show, handleClose }) {
  const { t } = useTranslation();

  const [newOrder, setNewOrder] = useState([]);
  const [data, setData] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);

  const fetchData = async () => {
    try {
      const dataResponse = await apiService.get(`api/v1/products/category/${categoryId}`);
      setData(dataResponse.filter((product) => !product.deleted).sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error("Error fetching data:", error);
      notificationService.notifyWarn(error.message);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const updatedRows = [...data];
    const [movedRow] = updatedRows.splice(result.source.index, 1);
    updatedRows.splice(result.destination.index, 0, movedRow);
    setData(updatedRows);

    const reordered = updatedRows.map((row, index) => ({
      id: row.id,
      order: (index + 1) * 10,
    }));
    setNewOrder(reordered);
  };

  const updatePrice = async (productId, price) => {
    try {
      await apiService.patch(`api/v1/products/${productId}`, { price });
      setData((prev) => prev.map((row) => (row.id === productId ? { ...row, price } : row)));
      notificationService.notifySuccess(t("messages.priceUpdatedSuccessfully"));
    } catch (error) {
      console.error(error);
      notificationService.notifyError(t("messages.unableToUpdateData"));
      throw error;
    }
  };

  const saveOrder = () => {
    setIsDisabled(true);
    apiService
      .postJson(`api/v1/products/order`, newOrder)
      .then((resp) => {
        notificationService.notifySuccess(t("messages.productOrderUpdatedSuccessfully"));
        setIsDisabled(false);
        fetchData();
      })
      .catch((error) => {
        setErrorMessage(error.message);
        notificationService.notifyError(error.message);
        setIsDisabled(false);
        handleClose();
      });
  };

  useEffect(() => {
    if (!categoryId) return;
    fetchData();
  }, [categoryId]);

  return (
    <>
      <Modal size="xl" dialogClassName="my-modal" show={show} onHide={handleClose} aria-labelledby="contained-modal-title-vcenter" centered>
        <Modal.Header closeButton>
          <Modal.Title as="h4">{t("pages.titles.editProductOrder")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="table-body">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="d-flex flex-column flex-wrap overflow-hidden">
                  <div className="row fw-bold px-2 py-2 bg-light rounded mb-2 mx-3 g-0">
                    <div className="col-12 col-sm-6 col-md-3">ID</div>
                    <div className="col-12 col-sm-6 col-md-3">{t("tables.headers.description")}</div>
                    <div className="col-12 col-sm-6 col-md-3">{t("tables.headers.price")}</div>
                    <div className="col-12 col-sm-6 col-md-3">{t("tables.headers.position")}</div>
                  </div>

                  {data.map((row, index) => (
                    <Draggable key={row.id} draggableId={row.id.toString()} index={index}>
                      {(provided) => (
                        <div
                          className="row bg-white shadow rounded px-2 py-2 mb-3 mx-3 g-0"
                          style={{ minHeight: "60px" }}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <div className="col-12 col-sm-6 col-md-3">{row.id}</div>
                          <div className="col-12 col-sm-6 col-md-3">{row.name}</div>
                          <div className="col-12 col-sm-6 col-md-3">
                            <PriceInput value={row.price} onCommit={(price) => updatePrice(row.id, price)} />
                          </div>
                          <div className="col-12 col-sm-6 col-md-3">
                            {row.order}
                            <i className="float-right pt-2 nc-icon nc-bold nc-preferences-circle-rotate"></i>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Modal.Body>
        <Modal.Footer>
          <Row className="w-100">
            <Col md="3" className="ml-auto">
              <SaveButton handler={saveOrder} disabled={isDisabled} />
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </>
  );
}
